<div class="warehouse-form core-screen">
  <div class="warehouse-form__main-content">
    @if (isLoading()) {
      <div class="warehouse-form__loading">
        {{ 'common.loading' | translate }}
      </div>
    }

    <form [formGroup]="form" class="warehouse-form__form" [class.warehouse-form__form--loading]="isLoading()">
      <!-- Name -->
      <lib-not-input
        type="text"
        formControlName="name"
        [label]="'admin.warehouses.create.nameLabel' | translate"
        [required]="true"
        [placeholder]="'admin.warehouses.create.namePlaceholder' | translate"
        [error]="getFieldError('name') | translate"
        [height]="'56px'"
      />

      <lib-not-input
        type="text"
        formControlName="slug"
        [label]="'admin.warehouses.create.slugLabel' | translate"
        [required]="true"
        [placeholder]="'admin.warehouses.create.slugPlaceholder' | translate"
        [error]="getFieldError('slug') | translate"
        [height]="'56px'"
      />
      <p class="warehouse-form__field-hint">
        {{ 'admin.warehouses.create.slugHint' | translate }}
      </p>

      <!-- City -->
      <lib-not-input
        type="text"
        formControlName="city"
        [label]="'admin.warehouses.create.cityLabel' | translate"
        [required]="true"
        [placeholder]="'admin.warehouses.create.cityExample' | translate"
        [error]="getFieldError('city') | translate"
        [height]="'56px'"
      />

      <!-- Address -->
      <lib-not-input
        type="text"
        formControlName="address"
        [label]="'admin.warehouses.create.addressLabel' | translate"
        [required]="true"
        [placeholder]="'admin.warehouses.create.addressExample' | translate"
        [error]="getFieldError('address') | translate"
        [height]="'56px'"
      />

      <!-- Longitude and Latitude (side by side) -->
      <div class="form-row">
        <lib-not-input
          type="text"
          inputmode="decimal"
          formControlName="longitude"
          [label]="'admin.warehouses.create.longitudeLabel' | translate"
          [placeholder]="'23.456' | translate"
          [error]="getFieldError('longitude') | translate"
          [height]="'56px'"
          mask="separator.6"
          [thousandSeparator]="''"
        />
        <lib-not-input
          type="text"
          inputmode="decimal"
          formControlName="latitude"
          [label]="'admin.warehouses.create.latitudeLabel' | translate"
          [placeholder]="'12.456' | translate"
          [error]="getFieldError('latitude') | translate"
          [height]="'56px'"
          mask="separator.6"
          [thousandSeparator]="''"
        />
      </div>

      <!-- Phone -->
      <lib-not-phone-input
        formControlName="phone"
        [countrySelectionEnabled]="true"
        [label]="'admin.warehouses.create.phoneLabel' | translate"
        [placeholder]="'(29) 111-11-11' | translate"
        [error]="phoneControlErrorText()"
        [height]="'56px'"
        (trigger)="unfocusedPhoneInput.next(true)"
      />

      <!-- Area, Height, Volume (side by side) -->
      <div class="form-row">
        <lib-not-input
          type="text"
          inputmode="decimal"
          formControlName="area_m2"
          [label]="'admin.warehouses.create.areaLabel' | translate"
          [placeholder]="'150' | translate"
          [error]="getFieldError('area_m2') | translate"
          [height]="'56px'"
          mask="separator.2"
          [thousandSeparator]="''"
        />
        <lib-not-input
          type="text"
          inputmode="decimal"
          formControlName="height_m"
          [label]="'admin.warehouses.create.heightLabel' | translate"
          [placeholder]="'4' | translate"
          [error]="getFieldError('height_m') | translate"
          [height]="'56px'"
          mask="separator.2"
          [thousandSeparator]="''"
        />
      </div>

      <lib-not-input
        type="text"
        inputmode="decimal"
        formControlName="useful_volume_m3"
        [label]="'admin.warehouses.create.volumeLabel' | translate"
        [placeholder]="'153' | translate"
        [error]="getFieldError('useful_volume_m3') | translate"
        [height]="'56px'"
        mask="separator.2"
        [thousandSeparator]="''"
      />

      <div class="form-row">
        <!-- Camera Count -->
        <lib-not-input
          type="text"
          inputmode="numeric"
          formControlName="camera_count"
          [label]="'admin.warehouses.create.cameraCountLabel' | translate"
          [placeholder]="'6' | translate"
          [error]="getFieldError('camera_count') | translate"
          [height]="'56px'"
          mask="9999999999"
        />

        <!-- Leak Sensor Count -->
        <lib-not-input
          type="text"
          inputmode="numeric"
          formControlName="leak_sensor_count"
          [label]="'admin.warehouses.create.leakSensorCountLabel' | translate"
          [placeholder]="'12' | translate"
          [error]="getFieldError('leak_sensor_count') | translate"
          [height]="'56px'"
          mask="9999999999"
        />
      </div>

      <!-- Has Heating (checkbox) -->
      <lib-not-input
        type="checkbox"
        formControlName="has_heating"
      >
        {{ 'admin.warehouses.create.hasHeatingLabel' | translate }}
      </lib-not-input>

      <!-- Rows Configuration Section -->
      <div class="warehouse-form__section">
        <div formArrayName="rows" class="warehouse-form__rows">
          @for (row of rowsArray.controls; track $index) {
            <div [formGroupName]="$index" class="warehouse-form__row-item">
              <div class="warehouse-form__row-header">
                <span class="warehouse-form__row-label">
                  {{ 'admin.warehouses.create.rowsSection.rowLabel' | translate }} {{ $index + 1 }}
                </span>
                <button
                  type="button"
                  class="warehouse-form__row-remove"
                  (click)="removeRow($index)"
                  [attr.aria-label]="'admin.warehouses.create.rowsSection.removeRow' | translate"
                >
                  ✕
                </button>
              </div>

              <div class="warehouse-form__row-fields">
                <lib-not-input
                  type="text"
                  inputmode="numeric"
                  formControlName="floors"
                  [label]="'admin.warehouses.create.rowsSection.floorsLabel' | translate"
                  [required]="true"
                  [placeholder]="'3' | translate"
                  [height]="'56px'"
                  mask="9999999999"
                />
                <lib-not-input
                  type="text"
                  inputmode="numeric"
                  formControlName="columns"
                  [label]="'admin.warehouses.create.rowsSection.columnsLabel' | translate"
                  [required]="true"
                  [placeholder]="'10' | translate"
                  [height]="'56px'"
                  mask="9999999999"
                />
              </div>

              <div class="warehouse-form__row-info">
                {{ 'admin.warehouses.create.rowsSection.cellsInRow' | translate }}:
                {{ row.get('floors')?.value * row.get('columns')?.value || 0 }}
              </div>
            </div>
          }
        </div>

        <lib-not-button
          type="button"
          [text]="'admin.warehouses.create.rowsSection.addRow' | translate"
          [action]="{ type: 'action' }"
          (actionEvent)="addRow()"
          [bgColor]="'transparent'"
          [color]="'#37beb0'"
          [borderColor]="'#37beb0'"
          [size]="'medium'"
          [width]="'100%'"
        />
      </div>
    </form>
  </div>

  <!-- Actions (fixed at bottom) -->
  <div class="warehouse-form__actions">
    <lib-cabinet-button
      type="submit"
      variant="primary"
      size="lg"
      [fullWidth]="true"
      [disabled]="isSubmitting() || isLoading() || (isEditMode() && !isFormChanged())"
      [isLoading]="isSubmitting()"
      [text]="(isEditMode() ? 'common.actions.update' : 'common.actions.create') | translate"
      [action]="{ type: 'action' }"
      (actionEvent)="onSubmit()"
    />
  </div>
</div>
